import { useState, useEffect, useMemo } from 'react';
import { Button, Segment, Message, Icon, Popup } from 'semantic-ui-react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import FlexBoxTmp from '../../../components/FlexBox';
import Loading from '../../../components/Loading';
import PassFailResult from '../../../components/PassFailResult';
import CenterCell from '../../../components/table/CenterCell';
import RightCell from '../../../components/table/RightCell';
import { getAPI } from '../../../libs/fetchAPI';
import BasicPaginatedTable from '../../../components/table/BasicPaginatedTable';
import Duration from '../../../components/Duration';
import SingleDetailsModal from '../../../components/SingleDetailsModal';
import IntroMessage from '../../../components/IntroMessage';
import JiraLink from '../../../components/JiraLink';
import RunCommand from '../../../components/RunCommand';

const FlexBox = styled(FlexBoxTmp)`
	justify-content: space-evenly;
	text-align: center;
`;

const Header = styled.h5`
	padding: 0;
	margin: 0 0 5px 0;
`;

const CustomSegment = styled(Segment)`
	margin-left: -10px !important;
	margin-right: -10px !important;
`;

const CustomSuiteTitle = styled.span`
	max-width: 75px !important;
`;

function SingleRun() {
	const { id }                                      = useParams();
	const [ loading, setLoading ]                     = useState(true);
	const [ loading_details, setLoadingDetails ]      = useState({});
	const [ run, setRun ]                             = useState([]);
	const [ suites, setSuites]                        = useState([]);
	const [ tests, setTests ]                         = useState([]);
	const [ test_errors, setTestErrors ]              = useState([]);
	const [ show_details_modal, setShowDetailsModal ] = useState(false);

	useEffect(() => {
		if(id !== `none`) {
			setLoading(true);

			Promise.all([getAPI(`/runs/${id}`), getAPI(`/runs/${id}/suites?limit=10000`)])
				.then((data) => {
					const [ run, suites ] = data;

					setRun(run.data[0]);

					// Sort the suites by failures first
					const sorted_suites = suites.data.sort(a => {
						return Number(a.failed) === 1 ? -1 : 0;
					});

					setSuites(sorted_suites);
					setLoading(false);
				});
		}
	}, [id]);

	function handleCloseDetails() {
		setShowDetailsModal(false);
	}

	function handleViewDetails(id) {
		setLoadingDetails({ [id] : true });

		Promise.all([
			getAPI(`/suites/${id}/tests`),
			getAPI(`/suites/${id}/errors`)
		]).then((response) => {
				const [ tmp_tests, tmp_errors ] = response;

				setTests(tmp_tests.data.reverse());
				setTestErrors(tmp_errors.data);
				setShowDetailsModal(true);
				setLoadingDetails({});
			});
	}

	const columns = useMemo(() => [
		{
			Header   : `Spec File`,
			accessor : `spec_file`,
			Cell     : ({ cell }) => <JiraLink issue_key={cell.value} />
		},
		{
			Header   : `Capabilities`,
			accessor : `capabilities`,
		},
		{
			Header   : `Suite Title`,
			accessor : `title`,
			Cell     : ({ cell }) => <CustomSuiteTitle>{cell.value}</CustomSuiteTitle>
		},
		{
			Header   : `Duration`,
			accessor : `duration`,
			Cell     : ({ cell }) => <RightCell><Duration duration={cell.value} /></RightCell>
		},
		{
			Header   : `Retries`,
			accessor : `retries`,
			collapse : true,
			Cell     : ({ cell }) => <RightCell>{cell.value}</RightCell>
		},
		{
			Header   : `Retry %`,
			accessor : `retry_percent`,
			collapse : true,
			Cell     : ({ cell }) => <RightCell>{cell.value}%</RightCell>
		},
		{
			Header         : `Result`,
			accessor       : `passed`,
			collapse       : true,
			disableFilters : true,
			Cell           : ({ cell }) => {
				if(Number(cell.value) === 1) {
					return <PassFailResult passed={cell.value} skipped={cell.row.values.skipped} />
				}

				return (
					<Popup
						trigger={<span><PassFailResult passed={cell.value} skipped={cell.row.values.skipped} /></span>}
						content={<Message negative><p>{cell.row.values.last_error}</p></Message>}
						on={[`hover`, `click`]}
					/>
				);
			}
		},
		{
			Header   : `Skipped Hidden`,
			accessor : `skipped`,
		},
		{
			Header   : `Last Error Hidden`,
			accessor : `last_error`,
		},
		{
			Header   : `Version Hidden`,
			accessor : `version`,
		},
		{
			Header         : ``,
			accessor       : `id`,
			disableFilters : true,
			Cell           : ({ cell }) => {
				const row    = cell.row.values;
				const params = {
					spec_file : row.spec_file,
					version   : row.version || ``,
				};

				const link = `${window.location.origin}/suites/single?${new URLSearchParams(params)}`;

				return (
					<CenterCell>
						<Button loading={loading_details[cell.value]} color="teal" onClick={() => handleViewDetails(cell.value)}>Details</Button>
						<a href={link} target="_blank" rel="noreferrer"><Button primary><span>History</span><span>&nbsp;&nbsp;<Icon name="external" /></span></Button></a>
						<RunCommand
							issue_key={run.title}
							spec_file={row.spec_file}
						/>
					</CenterCell>
				);
			}
		}
	], [loading_details, run]);

	if(!id || id === `none`) {
		return <Message>No run selected</Message>
	}

	if(loading) {
		return <Loading />
	}

	return (
		<>
			<SingleDetailsModal
				open={show_details_modal}
				handleClose={handleCloseDetails}
				tests={tests}
				errors={test_errors}
			/>

			<IntroMessage
				id={2}
				message='The Test Runs -> Selected Run page lists all the suites that were ran in the selected test run. Clicking the "Details" button will show all tests inside of a suite and any errors found. Clicking the "History" button will show all the history about an individual suite accross all test runs.'
			/>

			<RunSegment
				run={run}
			/>

			<BasicPaginatedTable
				columns={columns}
				data={suites}
				default_page_size="15"
				hidden_columns={[`skipped`, `last_error`, `version`]}
			/>
		</>
	);
}

function RunSegment({ run }) {
	return (
		<CustomSegment raised>
			<FlexBox>
				<div>
					<Header>Run Name</Header>
					<p><JiraLink issue_key={run.title} /></p>
				</div>
				<div>
					<Header>Run Date</Header>
					<p>{new Date(`${run.run_date} UTC`).toLocaleTimeString(`en-US`, {
						weekday: `long`, year: `numeric`, month: `short`, day: `numeric`, hour: `2-digit`, minute: `2-digit`, second: `2-digit`, hour12: true
					})}</p>
				</div>
				<div>
					<Header>Suites Ran</Header>
					<p>{run.suites_ran}</p>
				</div>
				<div>
					<Header>Duration</Header>
					<p><Duration duration={run.duration} /></p>
				</div>
				<div>
					<Header>Version</Header>
					<p>{run.version || `N/A`}</p>
				</div>
			</FlexBox>
		</CustomSegment>
	);
}

export default SingleRun;
